/**
 * Structured logger service
 * Provides consistent logging across the application
 */
import env from '../config/env.js'
import { originalConsole, setConsoleAdmin } from './consoleGuard.js'

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
}

// Only show debug logs in development
const currentLevel = env.isDev ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO

/**
 * Store admin status - updated externally by auth store
 * @type {{ isAdmin: boolean }}
 */
const adminStatus = {
  isAdmin: false,
}

/**
 * Update admin status - called by auth store when user state changes.
 * Also toggles the global console guard so raw console.* calls are gated.
 * @param {boolean} isAdmin
 */
export function setLoggerAdminStatus(isAdmin) {
  adminStatus.isAdmin = isAdmin
  setConsoleAdmin(isAdmin)
}

/**
 * Check if current user is admin
 */
function isAdminUser() {
  return adminStatus.isAdmin === true
}

/**
 * Format log message with timestamp and context
 */
function formatMessage(level, context, message) {
  const timestamp = new Date().toISOString()
  return `[${timestamp}] [${level}] [${context}] ${message}`
}

/**
 * Logger instance
 */
const logger = {
  /**
   * Debug level logging (dev only)
   * @param {string} context - Module or component name
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  debug(context, message, data = null) {
    if (currentLevel <= LOG_LEVELS.DEBUG && isAdminUser()) {
      const formatted = formatMessage('DEBUG', context, message)
      if (data) {
        originalConsole.debug(formatted, data)
      } else {
        originalConsole.debug(formatted)
      }
    }
  },

  /**
   * Info level logging
   * @param {string} context - Module or component name
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  info(context, message, data = null) {
    if (currentLevel <= LOG_LEVELS.INFO && isAdminUser()) {
      const formatted = formatMessage('INFO', context, message)
      if (data) {
        originalConsole.info(formatted, data)
      } else {
        originalConsole.info(formatted)
      }
    }
  },

  /**
   * Warning level logging
   * @param {string} context - Module or component name
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  warn(context, message, data = null) {
    if (currentLevel <= LOG_LEVELS.WARN && isAdminUser()) {
      const formatted = formatMessage('WARN', context, message)
      if (data) {
        originalConsole.warn(formatted, data)
      } else {
        originalConsole.warn(formatted)
      }
    }
  },

  /**
   * Error level logging
   * @param {string} context - Module or component name
   * @param {string} message - Log message
   * @param {Error|any} error - Error object or data
   */
  error(context, message, error = null) {
    if (currentLevel <= LOG_LEVELS.ERROR && isAdminUser()) {
      const formatted = formatMessage('ERROR', context, message)
      if (error) {
        originalConsole.error(formatted, error)
      } else {
        originalConsole.error(formatted)
      }
    }
  },

  /**
   * Create a scoped logger for a specific context
   * @param {string} context - Module or component name
   * @returns {Object} Scoped logger
   */
  createScoped(context) {
    return {
      debug: (message, data) => logger.debug(context, message, data),
      info: (message, data) => logger.info(context, message, data),
      warn: (message, data) => logger.warn(context, message, data),
      error: (message, error) => logger.error(context, message, error),
    }
  },
}

export default logger
